import {useCallback, useMemo, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {useFetchHook} from '@hooks/query-hook';
import {useStoreContext} from '@store/store-context';
import {FeedbackController} from '../feedback-controller';
import {FeedbackRequestImpl} from '../feedback-requests';
import {FeedbackRequest} from '../feedback-items';
import FeedbackFeed, {normaliseFeedbackResponse} from '../feedback-feed';
import {FeedbackFilterDivider, FeedbackFilters} from '../feedback-page.styles';
import {
  FeedbackDateControl,
  FeedbackFilterControl,
} from '../feedback-filter-control';
import {FeedbackDismissModal} from '../feedback-dismiss-modal';
import {useMyFeedbackHook} from './my-feedback-hook';
import {rememberDismissedFeedbackRequest} from '../feedback-dismissed-requests';

interface MyFeedbackProps {
  onRespond?: (request: FeedbackRequest) => void;
}

const MyFeedback = ({onRespond}: MyFeedbackProps) => {
  const {
    controller,
    dateRange,
    setDateRange,
    filters,
    selectedUser,
    setSelectedUser,
    sortBy,
    setSortBy,
  } = useMyFeedbackHook();
  const [type, setType] = useState<'all' | 'feedback' | 'praise'>('all');
  const [
    requestToDismiss,
    setRequestToDismiss,
  ] = useState<FeedbackRequest | null>(null);
  const [isDismissing, setIsDismissing] = useState(false);
  const {
    authStore: {auth},
    usersStore: {users},
    feedbackStore: {
      managerFeedbackRequests: feedbackRequests,
      updateFeedbackRequest,
    },
  } = useStoreContext();
  const workspaceId = auth.user.workspace?.id || 'unknown-workspace';

  const fetchFeedback = useCallback(
    async () => controller.getFeedback(filters, auth.user.id),
    [auth.user.id, controller, filters],
  );

  const {data, isLoading} = useFetchHook(
    ['my-feedback', filters],
    fetchFeedback,
  );
  const feedback = normaliseFeedbackResponse(data);
  const filteredFeedback = useMemo(
    () =>
      feedback.filter((item) => {
        const isPraise = Boolean(item.isPraise || item.group?.length);
        if (type === 'praise') return isPraise;
        if (type === 'feedback') return !isPraise;
        return true;
      }),
    [feedback, type],
  );
  const pendingRequests = (feedbackRequests as FeedbackRequest[]).filter(
    ({status}) => status !== 'completed',
  );
  const senderOptions = useMemo(
    () => [
      {value: '', label: 'Everyone'},
      ...users
        .filter(({id}) => id !== auth.user.id)
        .map((user) => ({
          value: user.id,
          label:
            user.firstName && user.lastName
              ? `${user.firstName} ${user.lastName}`
              : user.email,
        })),
    ],
    [auth.user.id, users],
  );

  const dismissRequest = useCallback(async () => {
    if (!requestToDismiss) return;

    setIsDismissing(true);
    try {
      const response = await new FeedbackController(
        new FeedbackRequestImpl(),
      ).deleteFeedbackRequest(requestToDismiss.id);
      if (response) {
        rememberDismissedFeedbackRequest(
          workspaceId,
          auth.user.id,
          requestToDismiss.id,
        );
        updateFeedbackRequest(
          (feedbackRequests as FeedbackRequest[]).filter(
            ({id}) => id !== requestToDismiss.id,
          ),
        );
        setRequestToDismiss(null);
      }
    } finally {
      setIsDismissing(false);
    }
  }, [
    auth.user.id,
    feedbackRequests,
    requestToDismiss,
    updateFeedbackRequest,
    workspaceId,
  ]);

  return (
    <div>
      <FeedbackFilters aria-label="Feedback filters">
        <div className="feedback-filter-group">
          <FeedbackFilterControl
            ariaLabel="Filter feedback sender"
            label="From"
            options={senderOptions}
            value={selectedUser}
            onChange={setSelectedUser}
            searchable
          />
          <FeedbackFilterDivider aria-hidden="true" />
          <FeedbackFilterControl
            ariaLabel="Filter feedback type"
            label="Type"
            options={[
              {value: 'all', label: 'All'},
              {value: 'feedback', label: 'Feedback'},
              {value: 'praise', label: 'Praise'},
            ]}
            value={type}
            onChange={(value) =>
              setType(value as 'all' | 'feedback' | 'praise')
            }
          />
        </div>
        <div className="feedback-filter-group">
          <FeedbackFilterControl
            ariaLabel="Sort feedback"
            label="Sort"
            options={[
              {value: 'desc', label: 'Newest First'},
              {value: 'asc', label: 'Oldest First'},
            ]}
            value={sortBy}
            onChange={setSortBy}
          />
          <FeedbackFilterDivider aria-hidden="true" />
          <FeedbackDateControl
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        </div>
      </FeedbackFilters>
      <FeedbackFeed
        variant="for-you"
        data={filteredFeedback}
        requests={pendingRequests}
        isLoading={isLoading}
        sortOrder={sortBy as 'asc' | 'desc'}
        pageKey={JSON.stringify([filters, selectedUser, type, sortBy])}
        emptyState={
          dateRange.preset !== 'all' || selectedUser || type !== 'all'
            ? {
                title: 'No matching feedback',
                description: 'Try changing the sender, type, or date filters.',
              }
            : undefined
        }
        onDismissRequest={setRequestToDismiss}
        onRespond={onRespond}
      />
      <FeedbackDismissModal
        request={requestToDismiss}
        isLoading={isDismissing}
        onClose={() => setRequestToDismiss(null)}
        onConfirm={dismissRequest}
      />
    </div>
  );
};

export default observer(MyFeedback);
